/**
 * SOLID Review
 * S — Good. The control panel's presentation: the table's facts it shows (the claim pickers' lists, the labels, the dice), the claim
 *     picked and the blind picker — both its round's — the moves' busy flags, and the moves themselves, each one use case. The
 *     pickers' rules, the challenger and every label are the Domain's (strict-ports FE1e).
 * O — Good. A new move is one more action and its busy flag.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Good. The panel's snapshot and its actions.
 * D — Strong. The Application's ports and the request contexts.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { ClientRequests } from '../client/ClientRequests';
import type { IPortClientStateView, TClientViewState } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

/** The moves that carry no command: the table names the round, the seat and the room. */
const BELIEVE: TClientUseCaseInput<'believe'> = {};
const LIFT: TClientUseCaseInput<'liftCup'> = {};
const ROLL_FIRST: TClientUseCaseInput<'rollFirst'> = {};
const PLAY_AGAIN: TClientUseCaseInput<'playAgain'> = {};
/** Back to the menu from a live table: the server is told the seat is free. */
const LEAVE_AND_TELL: TClientUseCaseInput<'leaveTable'> = { notifyServer: true };

type TView = TClientViewState;
type TDerived = TView['derived'];

/** What the panel reads of the client view — the selectors it held, one by one. */
interface IStateControlPanelReads {
  readonly phase: TView['phase'];
  readonly localUser: TView['localUser'];
  readonly activeRoundId: TView['activeRoundId'];
  readonly lastDiceRoll: TView['lastDiceRoll'];
  readonly diceRevealed: TView['diceRevealed'];
  readonly opponentClaim: TView['opponentClaim'];
  readonly revealResult: TView['revealResult'];
  readonly isRestoringSession: TView['isRestoringSession'];
  readonly isActiveRoller: TDerived['isActiveRoller'];
  readonly isChallenger: TDerived['isChallenger'];
  readonly legalClaims: TDerived['legalClaims'];
  readonly tacticalClaims: TDerived['tacticalClaims'];
  readonly honestClaims: TDerived['honestClaims'];
  readonly rolledLabel: TDerived['rolledLabel'];
  readonly opponentClaimDice: TDerived['opponentClaimDice'];
  readonly canBelieve: TDerived['canBelieve'];
  readonly revealLabels: TDerived['revealLabels'];
  readonly isGuest: boolean;
}

interface IStateControlPanelOwn {
  readonly showUsageReport: boolean;
  /** The claim picked — its round's: a new round clears it. */
  readonly claimedScore: number | null;
  readonly blindPickerOpen: boolean;
  readonly announcing: boolean;
  readonly believing: boolean;
  readonly lifting: boolean;
  readonly startingNewGame: boolean;
}

/** The panel as its view renders it. */
export interface IStateControlPanel extends IStateControlPanelReads, IStateControlPanelOwn {
  /** The opponent's claim as dice — rendered only with a claim, when the Domain always has them. */
  readonly claimDice: NonNullable<TDerived['opponentClaimDice']> | [];
}

const QUIET: IStateControlPanelOwn = {
  showUsageReport: false, claimedScore: null, blindPickerOpen: false, announcing: false, believing: false, lifting: false,
  startingNewGame: false,
};
const NEW_ROUND: Partial<IStateControlPanelOwn> = { claimedScore: null, blindPickerOpen: false };
const BLIND_PICKER: Partial<IStateControlPanelOwn> = { claimedScore: null, blindPickerOpen: true };
const NO_CLAIM: Partial<IStateControlPanelOwn> = { claimedScore: null };
const REPORT_OPEN: Partial<IStateControlPanelOwn> = { showUsageReport: true };
const REPORT_CLOSED: Partial<IStateControlPanelOwn> = { showUsageReport: false };

/**
 * THE CONTROL PANEL (frontend-framework-oo P4, 2026-10-29, §28.296) — `components/game/ControlPanel.tsx`'s state, its effect and its
 * handlers: the view keeps the markup. Every value is read through the client view and every move is one use case; the selection
 * belongs to its round — a new round, started, believed or rolled first, clears it.
 */
export class ControlPanelViewModel implements IServiceViewModel<IStateControlPanel> {
  private readonly state = new ObservableState<IStateControlPanelOwn>(QUIET);
  private readonly reactions = new Reactions<IStateControlPanel>([
    { deps: (panel) => [panel.activeRoundId], run: () => this.state.update(NEW_ROUND) },
  ]);
  private reads: IStateControlPanelReads | null = null;
  private shown: { readonly reads: IStateControlPanelReads; readonly own: IStateControlPanelOwn; readonly panel: IStateControlPanel } | null = null;

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'announce' | 'believe' | 'liftCup' | 'rollFirst' | 'playAgain' | 'leaveTable'>,
    private readonly view: IPortClientStateView,
    private readonly requests: ClientRequests,
  ) {}

  private busy(flag: keyof Pick<IStateControlPanelOwn, 'announcing' | 'believing' | 'lifting' | 'startingNewGame'>, on: boolean): void {
    const change: Partial<IStateControlPanelOwn> = { [flag]: on };
    this.state.update(change);
  }

  /** An open claim after a peek, a blind pass before one — the Application's plan chooses; a refused blind pass does not advance. */
  readonly announce = async (score: number): Promise<void> => {
    this.busy('announcing', true);
    const claim: TClientUseCaseInput<'announce'> = { score };
    await this.useCases.announce(this.requests.context(), claim);
    this.busy('announcing', false);
  };

  /** Meyer cannot be believed: the button is not rendered for it, and the plan refuses it too. */
  readonly believe = async (): Promise<void> => {
    this.busy('believing', true);
    await this.useCases.believe(this.requests.context(), BELIEVE);
    this.busy('believing', false);
  };

  readonly liftCup = async (): Promise<void> => {
    this.busy('lifting', true);
    await this.useCases.liftCup(this.requests.context(), LIFT);
    this.busy('lifting', false);
  };

  /** Roll First — the loser starts the next round with a clean floor (0 = any valid claim). */
  readonly rollFirst = async (): Promise<void> => {
    this.busy('believing', true);
    await this.useCases.rollFirst(this.requests.context(), ROLL_FIRST);
    this.busy('believing', false);
  };

  /**
   * Play Again — the last loser rolls first; a winner waits for the round's start. The new game's charge is the server's (§28.169)
   * and the payer's balance is pushed when it lands (the account watch, strict-ports FE4c) — no read after the start, which raced it.
   */
  readonly playAgain = async (): Promise<void> => {
    this.busy('startingNewGame', true);
    await this.useCases.playAgain(this.requests.context(), PLAY_AGAIN);
    this.busy('startingNewGame', false);
  };

  /** Leave the table — the server told, best effort — and back to the front screen, the identity kept (strict-ports FE2). */
  readonly backToMenu = async (): Promise<void> => {
    await this.useCases.leaveTable(this.requests.context(), LEAVE_AND_TELL);
  };

  readonly pickClaim = (claimedScore: number): void => {
    const picked: Partial<IStateControlPanelOwn> = { claimedScore };
    this.state.update(picked);
  };

  readonly clearClaim = (): void => this.state.update(NO_CLAIM);
  readonly openBlindPicker = (): void => this.state.update(BLIND_PICKER);
  readonly openUsageReport = (): void => this.state.update(REPORT_OPEN);
  readonly closeUsageReport = (): void => this.state.update(REPORT_CLOSED);

  private readsOf(view: TView): IStateControlPanelReads {
    const { derived } = view;
    const next: IStateControlPanelReads = {
      phase: view.phase, localUser: view.localUser, activeRoundId: view.activeRoundId, lastDiceRoll: view.lastDiceRoll,
      diceRevealed: view.diceRevealed, opponentClaim: view.opponentClaim, revealResult: view.revealResult,
      isRestoringSession: view.isRestoringSession, isActiveRoller: derived.isActiveRoller, isChallenger: derived.isChallenger,
      legalClaims: derived.legalClaims, tacticalClaims: derived.tacticalClaims, honestClaims: derived.honestClaims,
      rolledLabel: derived.rolledLabel, opponentClaimDice: derived.opponentClaimDice, canBelieve: derived.canBelieve,
      revealLabels: derived.revealLabels, isGuest: view.session.account.kind === 'guest',
    };
    const before = this.reads;
    const keys = Object.keys(next) as Array<keyof IStateControlPanelReads>;
    if (before !== null && keys.every((key) => Object.is(before[key], next[key]))) return before;
    this.reads = next;
    return next;
  }

  getSnapshot(): IStateControlPanel {
    const reads = this.readsOf(this.view.getSnapshot());
    const own = this.state.get();
    if (this.shown !== null && this.shown.reads === reads && this.shown.own === own) return this.shown.panel;
    const panel: IStateControlPanel = {
      ...reads,
      ...own,
      // The claim's dice block renders only when `opponentClaim` is set, and the Domain derives the dice from it in the same snapshot —
      // null exactly when it is null (scoreToDice never answers null). `?? []` is for the type, which cannot see that.
      /* c8 ignore next */
      claimDice: reads.opponentClaimDice ?? [],
    };
    this.shown = { reads, own, panel };
    return panel;
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(_inputs: readonly unknown[], rendered: IStateControlPanel): void {
    this.reactions.stopChanged(rendered);
  }

  commit(_inputs: readonly unknown[], rendered: IStateControlPanel): void {
    this.reactions.startChanged(rendered);
  }
}
